import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Shield, CheckCircle, Clock, XCircle, Upload, DollarSign, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerificationPage = () => {
  const navigate = useNavigate();
  const [verification, setVerification] = useState({
    id: 'not_started',
    background: 'not_started'
  });
  const [incomeVerified, setIncomeVerified] = useState(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [incomeAmount, setIncomeAmount] = useState('');

  useEffect(() => {
    fetchVerification();
  }, []);

  const fetchVerification = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setVerification({
        id: response.data.verification_id || 'not_started',
        background: response.data.verification_background || 'not_started'
      });
      setIncomeVerified(response.data.income_verified || null);
    } catch (error) {
      console.error('Error fetching verification:', error);
    }
  };

  const handleStartVerification = async (type) => {
    try {
      await axios.post(`${API}/verification/${type}`, null, { params: { status: 'pending' } });
      setVerification(prev => ({ ...prev, [type]: 'pending' }));

      // Simulate approval after 2 seconds
      setTimeout(async () => {
        await axios.post(`${API}/verification/${type}`, null, { params: { status: 'approved' } });
        setVerification(prev => ({ ...prev, [type]: 'approved' }));
      }, 2000);
    } catch (error) {
      console.error('Error starting verification:', error);
    }
  };

  const handleIncomeVerification = async () => {
    if (!incomeAmount) {
      alert('Please enter your annual income');
      return;
    }

    const income = parseFloat(incomeAmount.replace(/[^0-9.]/g, ''));
    
    try {
      // Start process
      await axios.post(`${API}/income-verification/start`);
      
      // Complete process
      const response = await axios.post(`${API}/income-verification/complete`, { income });
      setIncomeVerified(response.data.badge);
      setShowIncomeModal(false);
      setIncomeAmount('');
      
      alert(`Income verified! You earned the ${response.data.badge.replace('_', ' ')} badge.`);
    } catch (error) {
      console.error('Error verifying income:', error);
      alert('Income verification process started! Our team will review your information.');
      setShowIncomeModal(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'approved') return <CheckCircle size={24} style={{ color: '#28a745' }} />;
    if (status === 'pending') return <Clock size={24} style={{ color: '#D9B95F' }} />;
    if (status === 'failed') return <XCircle size={24} style={{ color: '#7A1E1E' }} />;
    return <Shield size={24} style={{ color: '#7A7A7A' }} />;
  };

  const getStatusColor = (status) => {
    if (status === 'approved') return '#28a745';
    if (status === 'pending') return '#D9B95F';
    if (status === 'failed') return '#7A1E1E';
    return '#7A7A7A';
  };

  const getStatusText = (status) => {
    if (status === 'approved') return 'Verified';
    if (status === 'pending') return 'Pending Review';
    if (status === 'failed') return 'Failed';
    return 'Not Started';
  };

  const renderDiamondBadge = (badge) => {
    const badges = {
      green_diamond: { 
        color: '#50C878', 
        label: 'Green Diamond', 
        subtitle: '$500k - $4M/yr',
        icon: '💎',
        gradient: 'linear-gradient(135deg, #50C878, #2E8B57)'
      },
      blue_diamond: { 
        color: '#4169E1', 
        label: 'Blue Diamond', 
        subtitle: '$5M - $10M/yr',
        icon: '💎',
        gradient: 'linear-gradient(135deg, #4169E1, #1E3A8A)'
      },
      black_diamond: { 
        color: '#1a1a1a', 
        label: 'Black Diamond', 
        subtitle: '$11M+/yr',
        icon: '💎',
        gradient: 'linear-gradient(135deg, #1a1a1a, #000000)'
      }
    };

    const info = badges[badge];
    if (!info) return null;

    return (
      <div style={{
        background: info.gradient,
        padding: '20px',
        borderRadius: '16px',
        border: `2px solid ${info.color}`,
        textAlign: 'center',
        boxShadow: `0 4px 20px ${info.color}44`,
        marginBottom: '24px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{info.icon}</div>
        <div style={{ 
          color: '#E6D7C3', 
          fontSize: '24px', 
          fontWeight: 'bold',
          fontFamily: 'Playfair Display, serif',
          marginBottom: '4px'
        }}>
          {info.label}
        </div>
        <div style={{ color: '#A57C33', fontSize: '14px' }}>
          {info.subtitle}
        </div>
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: '80px' }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: '#2C1B13',
        padding: '16px',
        borderBottom: '1px solid #A57C33'
      }}>
        <button
          onClick={() => navigate('/profile')}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px'
          }}
        >
          <ArrowLeft size={20} />
          Back to Profile
        </button>
      </div>

      <div style={{ padding: '20px' }}>
        <h1 style={{
          fontSize: '32px',
          color: '#E6D7C3',
          fontFamily: 'Playfair Display, serif',
          marginBottom: '8px'
        }}>
          Verification & Status
        </h1>
        <p style={{ color: '#A57C33', fontSize: '15px', marginBottom: '32px' }}>
          Verify your identity and income to build trust and unlock exclusive features
        </p>

        {/* Income Verification Section */}
        <div style={{
          background: 'rgba(215, 181, 109, 0.08)',
          padding: '20px',
          borderRadius: '16px',
          marginBottom: '24px',
          border: '2px solid #D7B56D',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
              padding: '12px',
              borderRadius: '12px'
            }}>
              <DollarSign size={28} style={{ color: '#2C1B13' }} />
            </div>
            <div>
              <h2 style={{
                fontSize: '22px',
                color: '#D7B56D',
                fontFamily: 'Playfair Display, serif',
                margin: 0
              }}>
                Income Verification
              </h2>
              <p style={{ color: '#A57C33', fontSize: '13px', margin: 0 }}>
                Earn exclusive diamond badges
              </p>
            </div>
          </div>

          {incomeVerified ? (
            renderDiamondBadge(incomeVerified)
          ) : (
            <div>
              <p style={{ color: '#E6D7C3', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px' }}>
                Verify your income to earn a prestigious diamond badge displayed on your profile. This helps build trust and shows your success level.
              </p>

              <div style={{
                background: 'rgba(44, 27, 19, 0.5)',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: '#50C878', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                    💎 Green Diamond
                  </div>
                  <div style={{ color: '#A57C33', fontSize: '13px' }}>
                    $500,000 - $4,000,000 / year
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: '#4169E1', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                    💎 Blue Diamond
                  </div>
                  <div style={{ color: '#A57C33', fontSize: '13px' }}>
                    $5,000,000 - $10,000,000 / year
                  </div>
                </div>
                <div>
                  <div style={{ color: '#E6D7C3', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                    💎 Black Diamond
                  </div>
                  <div style={{ color: '#A57C33', fontSize: '13px' }}>
                    $11,000,000+ / year
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowIncomeModal(true)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
                  color: '#2C1B13',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(215, 181, 109, 0.4)'
                }}
              >
                Start Income Verification
              </button>
            </div>
          )}
        </div>

        {/* ID Verification */}
        <div style={{
          background: 'rgba(215, 181, 109, 0.08)',
          padding: '20px',
          borderRadius: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(165, 124, 51, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                color: '#E6D7C3',
                fontFamily: 'Playfair Display, serif',
                marginBottom: '4px'
              }}>
                ID Verification
              </h3>
              <p style={{ color: '#A57C33', fontSize: '14px', margin: 0 }}>
                Verify your identity with a government ID
              </p>
            </div>
            {getStatusIcon(verification.id)}
          </div>

          <div style={{
            padding: '12px',
            background: 'rgba(44, 27, 19, 0.5)',
            borderRadius: '8px',
            marginBottom: '12px',
            border: `1px solid ${getStatusColor(verification.id)}33`
          }}>
            <div style={{
              color: getStatusColor(verification.id),
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              Status: {getStatusText(verification.id)}
            </div>
          </div>

          {verification.id === 'not_started' && (
            <button
              onClick={() => handleStartVerification('id')}
              style={{
                width: '100%',
                padding: '12px',
                background: '#D7B56D',
                color: '#2C1B13',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Start Verification
            </button>
          )}
        </div>

        {/* Background Check - Coming Soon */}
        <div 
          data-testid="background-check-card"
          style={{
            background: 'rgba(215, 181, 109, 0.08)',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid rgba(165, 124, 51, 0.3)'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                color: '#E6D7C3',
                fontFamily: 'Playfair Display, serif',
                marginBottom: '8px'
              }}>
                Background Check
              </h3>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
                borderRadius: '12px'
              }}>
                <Clock size={12} color="#E9D8FD" />
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#E9D8FD',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Coming Soon
                </span>
              </div>
            </div>
            <Shield size={24} color="#A57C33" />
          </div>

          <p style={{ 
            color: '#A57C33', 
            fontSize: '14px', 
            margin: '0 0 16px 0',
            lineHeight: '1.5' 
          }}>
            We're partnering with leading background check providers to bring you comprehensive 
            safety verification. Background checks will include criminal history, identity verification, 
            and additional safety screening.
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '16px',
            background: 'rgba(107, 70, 193, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(107, 70, 193, 0.2)'
          }}>
            <Shield size={20} color="#6B46C1" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <div style={{ color: '#6B46C1', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                Enhanced Safety Features
              </div>
              <ul style={{ 
                color: '#A57C33', 
                fontSize: '13px', 
                margin: 0, 
                paddingLeft: '16px',
                lineHeight: '1.6'
              }}>
                <li>Criminal background screening</li>
                <li>Identity verification confirmation</li>
                <li>Sex offender registry check</li>
                <li>Trusted badge on your profile</li>
              </ul>
            </div>
          </div>

          <button
            disabled
            data-testid="background-check-coming-soon-btn"
            style={{
              width: '100%',
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(165, 124, 51, 0.2)',
              color: '#A57C33',
              border: '1px solid rgba(165, 124, 51, 0.3)',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: 'not-allowed',
              opacity: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Clock size={18} />
            Notify Me When Available
          </button>
        </div>

        {/* Benefits Info */}
        <div style={{
          marginTop: '24px',
          padding: '20px',
          background: 'rgba(106, 155, 216, 0.08)',
          borderRadius: '16px',
          border: '1px solid rgba(106, 155, 216, 0.3)'
        }}>
          <div style={{
            color: '#6A9BD8',
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Award size={20} />
            Verification Benefits
          </div>
          <ul style={{ color: '#E6D7C3', fontSize: '14px', lineHeight: '1.8', margin: 0, paddingLeft: '20px' }}>
            <li>Build trust with other members</li>
            <li>Get a verified badge on your profile</li>
            <li>Stand out in search results</li>
            <li>Access exclusive events and features</li>
            <li>Show your commitment to safety</li>
          </ul>
        </div>

        {/* Premium KYC Providers - Coming Soon */}
        <div 
          data-testid="kyc-providers-coming-soon"
          style={{
            marginTop: '24px',
            padding: '20px',
            background: 'rgba(107, 70, 193, 0.08)',
            borderRadius: '16px',
            border: '1px solid rgba(107, 70, 193, 0.25)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{
              color: '#9F7AEA',
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Shield size={20} />
              Premium KYC Verification
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
              borderRadius: '12px'
            }}>
              <Clock size={12} color="#E9D8FD" />
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#E9D8FD',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Coming Soon
              </span>
            </div>
          </div>
          
          <p style={{ color: 'rgba(244, 231, 195, 0.8)', fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px 0' }}>
            We're integrating with industry-leading KYC providers for enhanced identity verification and fraud protection.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <div style={{
              padding: '12px',
              background: 'rgba(107, 70, 193, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(107, 70, 193, 0.2)'
            }}>
              <div style={{ color: '#9F7AEA', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Plaid Identity
              </div>
              <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '12px', margin: 0 }}>
                Bank-grade document & selfie verification
              </p>
            </div>
            <div style={{
              padding: '12px',
              background: 'rgba(107, 70, 193, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(107, 70, 193, 0.2)'
            }}>
              <div style={{ color: '#9F7AEA', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Jumio
              </div>
              <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '12px', margin: 0 }}>
                3D liveness & biometric matching
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Income Verification Modal */}
      {showIncomeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#2C1B13',
            padding: '24px',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            border: '2px solid #D7B56D',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                color: '#D7B56D',
                fontSize: '24px',
                fontFamily: 'Playfair Display, serif',
                margin: 0
              }}>
                Income Verification
              </h2>
              <button
                onClick={() => setShowIncomeModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#E6D7C3',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
              Enter your annual income. We'll securely verify this through financial integrations. Your information is encrypted and never shared.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                Annual Income (USD)
              </label>
              <input
                type="text"
                value={incomeAmount}
                onChange={(e) => setIncomeAmount(e.target.value)}
                placeholder="e.g., 750000"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(230, 215, 195, 0.08)',
                  border: '1px solid #D7B56D',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{
              background: 'rgba(215, 181, 109, 0.08)',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#A57C33',
              lineHeight: '1.5'
            }}>
              🔒 Your information is secure and private. We use bank-level encryption to protect your data.
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowIncomeModal(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'rgba(230, 215, 195, 0.08)',
                  color: '#E6D7C3',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleIncomeVerification}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
                  color: '#2C1B13',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(215, 181, 109, 0.4)'
                }}
              >
                Verify Income
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationPage;
